import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import {Person, Refresh, VideoCall, Warning} from '@mui/icons-material';
import {useRayzayUIController} from '../../context';
import {getMyChildren} from '../../api/children/childrenApiClient';
import {
    generateMJPEGStreamingToken,
    getChildMappedCamerasMJPEG,
    getKidCameraMJPEGStreamUrl
} from '../../api/kidMJPEGStreaming/kidMJPEGStreamingApiClient';
import KidMJPEGPlayer from '../../components/KidMJPEGPlayer/KidMJPEGPlayer';
import DashboardLayout from '../../custom/LayoutContainers/DashboardLayout';
import {getAllBranches} from "../../api/branch/branchApiClient";
import {getAllRoomsByBranchId} from "../../api/room/roomApiClient";

/**
 * Kid MJPEG Streaming Component
 * Shows child list and video player for MJPEG streaming with face masking via Python service
 * Flow: Load children -> Select child -> Load cameras -> Select camera -> Stream MJPEG
 */
const KidMJPEGStreaming = () => {
  const [controller] = useRayzayUIController();
  const { loggedInUserDetail, isAuthenticated } = controller;

  // Pagination: load in chunks to avoid timeout and double load
  const PAGE_SIZE = 20;
  const initialLoadDoneRef = useRef(false);

  // State management
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(-1);
  const [childCameras, setChildCameras] = useState([]);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [streamToken, setStreamToken] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);

  // Filter state
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);

  const scrollSentinelRef = useRef(null);
  const streamImgRef = useRef(null); // ref to player's img so we can clear src on unmount/hide
  const location = useLocation();

  /**
   * Filter children by branch and/or room
   * @param {Array} children - Array of child objects
   * @param {number|null} branchId - Branch ID to filter by (null/undefined = no branch filter)
   * @param {number|null} roomId - Room ID to filter by (null/undefined = no room filter)
   * @returns {Array} Filtered children array
   */
  const filterChildrenByBranchAndRoom = (children, branchId, roomId) => {
    const hasBranchFilter = branchId != null && branchId !== "";
    const hasRoomFilter = roomId != null && roomId !== "";

    if (!hasBranchFilter && !hasRoomFilter) return children;

    return children.filter((child) => {
      // If branchId is set, child must match
      if (hasBranchFilter && child.branchId !== branchId) {
        return false;
      }
      // If roomId is set, child must match (only check if branch passes)
      if (hasRoomFilter && child.roomId !== roomId) {
        return false;
      }
      return true;
    });
  };

  /**
   * Load first page of children (reset list). Used on mount and refresh.
   */
  const loadChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    setChildren([]);
    setCurrentPage(-1);
    try {
      // Build query params with filters
      const params = {
        paginated: true,
        page: 0,
        size: PAGE_SIZE,
      };

      // Add branch and room filters to query params
      if (selectedBranchId) {
        params.branchId = selectedBranchId;
      }
      if (selectedRoomId) {
        params.roomId = selectedRoomId;
      }

      const data = await getMyChildren(params);
      const childrenData = data.children || [];

      setChildren(childrenData);
      setCurrentPage(0);
      setTotalElements(data.totalElements ?? childrenData.length);
      setHasNextPage(Boolean(data.hasNext));
    } catch (error) {
      console.error("Error loading children:", error);
      setChildren([]);
    } finally {
      setIsLoadingChildren(false);
    }
  }, [selectedBranchId, selectedRoomId]);

  /**
   * Load next page and append (for infinite scroll)
   */
  const loadMoreChildren = useCallback(async () => {
    if (isLoadingMore || !hasNextPage || isLoadingChildren) return;
    const nextPage = currentPage + 1;
    try {
      setIsLoadingMore(true);

      // Build query params with filters
      const params = {
        paginated: true,
        page: nextPage,
        size: PAGE_SIZE,
      };

      // Add branch and room filters to query params
      if (selectedBranchId) {
        params.branchId = selectedBranchId;
      }
      if (selectedRoomId) {
        params.roomId = selectedRoomId;
      }

      const data = await getMyChildren(params);
      const childrenData = data.children || [];
      setChildren((prev) => [...prev, ...childrenData]);
      setCurrentPage(nextPage);
      setTotalElements(data.totalElements ?? totalElements);
      setHasNextPage(Boolean(data.hasNext));
    } catch (error) {
      console.error("Error loading more children:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    currentPage,
    hasNextPage,
    isLoadingMore,
    isLoadingChildren,
    totalElements,
    selectedBranchId,
    selectedRoomId,
  ]);

  /**
     * Stop streaming and cleanup resources
     */
  const stopStreaming = useCallback(() => {
    setStreamUrl(null);
    setSelectedCamera(null);
    console.log("MJPEG streaming stopped");
  }, []);

  /**
   * Get child cameras
   */
  const getChildCameras = useCallback(async (childId) => {
    console.log("Fetching child mapped cameras for child id: ", childId);
    setIsLoadingCameras(true);
    try {
      // Generate token for secure streaming
      const tokenResponse = await generateMJPEGStreamingToken(childId);
      if (!tokenResponse.success) {
        throw new Error(`Token generation failed: ${tokenResponse.message}`);
      }
      const token = tokenResponse.token;
      setStreamToken(token);

      // Get active cameras - uses JWT auth, doesn't need token parameter
      const response = await getChildMappedCamerasMJPEG(childId);
      console.log("Camera response:", response);

      if (
        response?.activeCameras &&
        Array.isArray(response.activeCameras) &&
        response.activeCameras.length > 0
      ) {
        setChildCameras(response.activeCameras);
      } else {
        setChildCameras([]);
      }
    } catch (error) {
      console.error("Error fetching child mapped cameras: ", error);
      setChildCameras([]);
    } finally {
      setIsLoadingCameras(false);
    }
  }, []);

  /**
   * Handle child selection and load cameras
   */
  const handleChildSelect = useCallback(
    async (child) => {
      // Stop any existing streams
      stopStreaming();

      // Select the new child and load cameras
      setSelectedChild(child);
      setSelectedCamera(null);
      setChildCameras([]);
      setStreamToken(null);
      setStreamUrl(null);

      await getChildCameras(child.childId);
    },
    [stopStreaming, getChildCameras]
  );

  /**
   * Handle camera click to start streaming for that camera
   * Flow: 1. Get/refresh temp token 2. Use token to get stream URL 3. Play via image tag
   */
  const handleCameraSelect = useCallback(
    async (camera) => {
      if (!selectedChild) return;

      try {
        // If already streaming (possibly another camera), stop current stream
        if (streamUrl) {
          // Abort current MJPEG request immediately (don't wait for React unmount/state)
          if (streamImgRef.current) streamImgRef.current.src = "";
          stopStreaming();
          // Wait a bit for cleanup
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        setSelectedCamera(camera);

        console.log("=== Camera Selected - Starting Stream Flow ===");
        console.log("Step 1: Getting/refreshing temp token for child:", selectedChild.childId);

        // Step 1: Get/refresh temp token
        let token = streamToken;
        if (!token) {
          console.log("No existing token, generating new token...");
          const tokenResponse = await generateMJPEGStreamingToken(selectedChild.childId);
          if (!tokenResponse.success) {
            throw new Error(`Token generation failed: ${tokenResponse.message}`);
          }
          token = tokenResponse.token;
          setStreamToken(token);
          console.log("Token generated successfully");
        } else {
          console.log("Using existing token");
        }

        // Step 2: Build stream URL with token
        const cameraId = camera.id ?? camera.cameraId ?? camera.channelId;
        if (!cameraId) {
          throw new Error("Camera identifier is missing");
        }

        console.log(
          "Step 2: Building stream URL for child:",
          selectedChild.childId,
          "camera:",
          cameraId
        );
        const streamUrlValue = getKidCameraMJPEGStreamUrl(
          selectedChild.childId,
          cameraId,
          token,
          "frontend-user"
        );
        console.log("Stream URL built:", streamUrlValue);

        // Step 3: Set stream URL - the player component will handle the rest
        setStreamUrl(streamUrlValue);
        console.log("=== Stream Flow Initiated ===");
      } catch (error) {
        console.error("Error starting camera stream:", error);
      }
    },
    [selectedChild, streamToken, streamUrl, stopStreaming]
  );

  // Load first page once when authenticated (ref prevents double load from Strict Mode / deps)
  useEffect(() => {
    if (!isAuthenticated || !loggedInUserDetail) return;
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    loadChildren();

    // Load branches for filter on mount
    const loadBranches = async () => {
      try {
        setIsLoadingBranches(true);
        const response = await getAllBranches();
        const branchesData = response.data || [];
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        console.error("Error loading branches:", error);
      } finally {
        setIsLoadingBranches(false);
      }
    };
    loadBranches();
    return () => {
      initialLoadDoneRef.current = false;
      console.log("KidMJPEGStreaming component unmounting, cleaning up streams...");
      // Clear img src immediately so browser aborts request and backend (Java/Python) sees disconnect
      if (streamImgRef.current) {
        streamImgRef.current.src = "";
        streamImgRef.current = null;
      }
      stopStreaming();
    };
  }, [isAuthenticated, loggedInUserDetail, loadChildren, stopStreaming]);

  // Load rooms when branch selection changes
  useEffect(() => {
    if (selectedBranchId) {
      const loadRooms = async () => {
        try {
          setIsLoadingRooms(true);
          const response = await getAllRoomsByBranchId(selectedBranchId);
          setRooms(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
          console.error("Error loading rooms:", error);
          setRooms([]);
        } finally {
          setIsLoadingRooms(false);
        }
      };
      loadRooms();
    } else {
      setRooms([]);
      setSelectedRoomId(null);
    }
  }, [selectedBranchId]);

  // Handle page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (streamUrl) {
        console.log("Page unloading, stopping stream");
        stopStreaming();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [streamUrl, stopStreaming]);

  /**
   * Check if user can access this component
   */
  const canAccess = () => {
    return isAuthenticated && loggedInUserDetail && loggedInUserDetail.role !== "STAFF";
  };

  // Access denied for staff
  if (!canAccess()) {
    return (
      <Box sx={{ mt: 4, p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" component="div">
            Access Denied
          </Typography>
          <Typography>
            Staff members are not authorized to view kid face detection streams.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          <VideoCall sx={{ mr: 2, verticalAlign: "middle" }} />
          FACE DETECTION TEST LAB
        </Typography>

        <Grid container spacing={3}>
          {/* Children List Panel - Left Side */}
          <Grid item xs={12} md={3}>
            <Card elevation={3} sx={{ height: "100%" }}>
              <CardContent>
                {/* Filter Section */}
                <Box mb={2}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="text.secondary"
                    gutterBottom
                  >
                    Filters
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Branch Filter */}
                    <Grid item xs={12}>
                      <Box mb={1}>
                        <Typography
                          component="label"
                          variant="caption"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          Branch
                        </Typography>
                        <Select
                          value={selectedBranchId || ""}
                          onChange={(e) => {
                            setSelectedBranchId(e.target.value);
                            setSelectedRoomId(null); // Reset room when branch changes
                          }}
                          displayEmpty
                          fullWidth
                          size="small"
                          disabled={isLoadingBranches}
                        >
                          <MenuItem value="">
                            <em>All Branches</em>
                          </MenuItem>
                          {isLoadingBranches ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Loading...
                            </MenuItem>
                          ) : branches.length === 0 ? (
                            <MenuItem disabled>No branches available</MenuItem>
                          ) : (
                            branches.map((branch) => (
                              <MenuItem key={branch.branchId} value={branch.branchId}>
                                {branch.location || branch.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </Box>
                    </Grid>

                    {/* Room Filter */}
                    <Grid item xs={12}>
                      <Box mb={1}>
                        <Typography
                          component="label"
                          variant="caption"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          Classroom (Room)
                        </Typography>
                        <Select
                          value={selectedRoomId || ""}
                          onChange={(e) => setSelectedRoomId(e.target.value)}
                          displayEmpty
                          fullWidth
                          size="small"
                          disabled={isLoadingRooms}
                        >
                          <MenuItem value="">
                            <em>All Classrooms</em>
                          </MenuItem>
                          {isLoadingRooms ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Loading...
                            </MenuItem>
                          ) : rooms.length === 0 ? (
                            <MenuItem disabled>No classrooms available</MenuItem>
                          ) : (
                            rooms.map((room) => (
                              <MenuItem key={room.roomId} value={room.roomId}>
                                {room.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </Box>
                    </Grid>
                  </Grid>

                  {(selectedBranchId || selectedRoomId) && (
                    <Box mt={1}>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => {
                          setSelectedBranchId(null);
                          setSelectedRoomId(null);
                        }}
                        startIcon={<Refresh />}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {isLoadingChildren ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="200px"
                  >
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body1" color="textSecondary">
                      Loading children...
                    </Typography>
                  </Box>
                ) : children.length === 0 ? (
                  <Alert severity="info">
                    {selectedBranchId || selectedRoomId
                      ? "No children found for the selected filters. Try clearing filters."
                      : "No children found for your role and permissions."}
                  </Alert>
                ) : (
                  <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Showing {children.length} of {totalElements} children
                    </Typography>
                    <List>
                      {children.map((child) => (
                        <ListItem key={child.childId} disablePadding>
                          <ListItemButton
                            onClick={() => handleChildSelect(child)}
                            selected={selectedChild?.childId === child.childId}
                            sx={{
                              borderRadius: 1,
                              mb: 1,
                              "&.Mui-selected": {
                                bgcolor: "primary.light",
                                "&:hover": {
                                  bgcolor: "primary.light",
                                },
                              },
                            }}
                          >
                            <Avatar
                              sx={{ mr: 2, width: 32, height: 32 }}
                              src={
                                child.profilePic
                                  ? `data:image/png;base64,${child.profilePic}`
                                  : null
                              }
                            >
                              {!child.profilePic && child.firstName?.charAt(0)}
                            </Avatar>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {child.firstName} {child.lastName}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  {child.branchName && (
                                    <Chip
                                      label={`Branch: ${child.branchName}`}
                                      size="small"
                                      sx={{
                                        fontSize: "0.75rem",
                                        height: 20,
                                        mr: 0.5,
                                        "& .MuiChip-label": {
                                          fontSize: "0.75rem",
                                        },
                                        bgcolor: "primary.light",
                                        color: "primary.dark",
                                      }}
                                    />
                                  )}
                                  {child.roomName && (
                                    <Chip
                                      label={`Room: ${child.roomName}`}
                                      size="small"
                                      sx={{
                                        fontSize: "0.75rem",
                                        height: 20,
                                        mr: 0.5,
                                        "& .MuiChip-label": {
                                          fontSize: "0.75rem",
                                        },
                                        bgcolor: "info.light",
                                        color: "info.dark",
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                            />
                            {selectedChild?.childId === child.childId && (
                              <Chip label="Selected" color="primary" size="small" />
                            )}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                    <div
                      ref={scrollSentinelRef}
                      style={{ height: 1, minHeight: 1 }}
                      aria-hidden="true"
                    />
                    {isLoadingMore && (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {hasNextPage && !isLoadingMore && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        textAlign="center"
                        py={1}
                      >
                        Scroll for more
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Video Player Panel - Right Side with Cameras on Top */}
          <Grid item xs={12} md={9}>
            {/* Camera Selection Tiles - Above Video Player */}
            {selectedChild && (
              <Card elevation={3} sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      <VideoCall sx={{ mr: 1, verticalAlign: "middle" }} />
                      Available Cameras for {selectedChild.firstName}
                    </Typography>
                  </Box>
                  {isLoadingCameras ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="150px"
                    >
                      <CircularProgress sx={{ mb: 2 }} />
                      <Typography variant="body1" color="textSecondary">
                        Loading cameras...
                      </Typography>
                    </Box>
                  ) : childCameras && childCameras.length > 0 ? (
                    <Grid container spacing={2}>
                      {childCameras.map((cam) => {
                        const cameraId = cam.id ?? cam.cameraId ?? cam.channelId;
                        const channelId = cam.channelId ?? cam.cameraId ?? cam.id;
                        const isSelected =
                          selectedCamera &&
                          (selectedCamera.id === cameraId ||
                            selectedCamera.cameraId === cameraId ||
                            selectedCamera.channelId === cameraId);

                        return (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={cameraId}>
                            <Card
                              sx={{
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                border: isSelected ? "3px solid" : "2px solid",
                                borderColor: isSelected
                                  ? "primary.main"
                                  : cam.camBranchActive
                                  ? "success.light"
                                  : "grey.300",
                                bgcolor: isSelected
                                  ? "primary.light"
                                  : cam.camBranchActive
                                  ? "success.50"
                                  : "grey.50",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                overflow: "visible",
                                "&:hover": {
                                  transform: "translateY(-6px)",
                                  boxShadow: isSelected ? 8 : 6,
                                  borderColor: "primary.main",
                                  bgcolor: isSelected ? "primary.light" : "primary.50",
                                },
                                boxShadow: isSelected
                                  ? "0 8px 16px rgba(25, 118, 210, 0.3)"
                                  : cam.camBranchActive
                                  ? "0 2px 8px rgba(46, 125, 50, 0.2)"
                                  : "0 2px 4px rgba(0, 0, 0, 0.1)",
                              }}
                              onClick={() => handleCameraSelect(cam)}
                            >
                              {isSelected && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -8,
                                    right: 12,
                                    bgcolor: "primary.main",
                                    color: "white",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                    zIndex: 1,
                                    boxShadow: 2,
                                  }}
                                >
                                  LIVE
                                </Box>
                              )}
                              <CardContent
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  p: 2.5,
                                  "&:last-child": {
                                    pb: 2.5,
                                  },
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={2} flexWrap="nowrap">
                                  <Box
                                    sx={{
                                      bgcolor: cam.camBranchActive ? "success.main" : "grey.400",
                                      borderRadius: "50%",
                                      p: 1.5,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      boxShadow: 2,
                                    }}
                                  >
                                    <VideoCall
                                      sx={{
                                        fontSize: 28,
                                        color: "white",
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Box>
                                  <Box minWidth={0} flex={1}>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: "1.1rem",
                                        color: "text.primary",
                                        mb: 0.5,
                                      }}
                                    >
                                      {cam.roomName}
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: "1.1rem",
                                        color: "text.primary",
                                        mb: 0.5,
                                      }}
                                    >
                                      Channel {channelId}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                      <Chip
                                        label={cam.camBranchActive ? "Active" : "Inactive"}
                                        color={cam.camBranchActive ? "success" : "default"}
                                        size="small"
                                        sx={{
                                          fontWeight: "medium",
                                          fontSize: "0.75rem",
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="200px"
                      sx={{
                        bgcolor: "warning.light",
                        borderRadius: 2,
                        p: 4,
                        border: "1px solid",
                        borderColor: "warning.main",
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: "warning.main",
                          borderRadius: "50%",
                          width: 64,
                          height: 64,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <Warning sx={{ fontSize: 40, color: "white" }} />
                      </Box>
                      <Typography
                        variant="h6"
                        color="text.primary"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        No Cameras Available
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ maxWidth: "500px", mt: 1 }}
                      >
                        The branch is not currently operating or there are no cameras configured for
                        streaming.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Video Player Card - Only show if child is selected and cameras are available */}
            {!selectedChild ? (
              <Card elevation={3}>
                <CardContent>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="400px"
                    bgcolor="grey.100"
                    borderRadius={1}
                  >
                    <VideoCall sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      Select a child to view cameras
                    </Typography>
                    <Typography variant="body2" color="textSecondary" align="center">
                      Then click a camera channel to start streaming
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              selectedChild &&
              !isLoadingCameras &&
              childCameras &&
              childCameras.length > 0 && (
                <Card elevation={3}>
                  <CardContent>
                    <Divider sx={{ my: 2 }} />

                    {!selectedCamera ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="400px"
                        bgcolor="grey.100"
                        borderRadius={1}
                      >
                        <VideoCall sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                          Select a camera to start streaming
                        </Typography>
                        <Typography variant="body2" color="textSecondary" align="center">
                          Click one of the camera tiles above to begin
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <KidMJPEGPlayer
                          streamUrl={streamUrl || ""}
                          childName={
                            selectedChild
                              ? `${selectedChild.firstName} ${selectedChild.lastName}`
                              : ""
                          }
                          cameraInfo={
                            selectedCamera
                              ? `Channel ${
                                  selectedCamera.channelId ??
                                  (selectedCamera.id || selectedCamera.cameraId)
                                }`
                              : ""
                          }
                          onError={(error) => {
                            console.error("Stream error:", error);
                          }}
                          onLoad={() => {
                            console.log("Stream loaded successfully");
                          }}
                          onStop={stopStreaming}
                          showControls={true}
                          showLoading={true}
                          style={{ minHeight: "500px" }}
                          streamImgRef={streamImgRef}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          Streams automatically stop after 10 minutes. Select the camera again to
                          continue.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )
            )}
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};

export default KidMJPEGStreaming;

